import React, { useState, useEffect } from 'react'
import { CuiButton } from 'front-lib'
import moment from 'moment'
import Modal from '../../../../components/NewModal'
import RateableItem from '../../../../components/RateableItem'
import { CTA_WORDING } from '../../../../modules/Share/constants'
import { getDataProduct } from './utils'
import { useCreateUserReview } from 'src/hooks/useCreateUserReview'
import './ReviewModal.scss'

interface Product {
  id: string
  name: string
  short_description: string
  image: string
  review?: ReviewData
  category?: string
}

interface OrderItem {
  product: Partial<Product>
}

interface Order {
  id: string
  order_creation_id: string
  display_date: string
  items: OrderItem[]
}

interface ReviewData {
  productId: string
  rating: number
  review: string
  reasons: string[]
}

interface ReviewInput {
  rating: number
  review: string
  reasons: string[]
}

interface ReviewModalProps {
  order?: Order
  controlled?: boolean
  isOpen?: boolean
  toggleShareModal?: (data: { stars: number } & Product) => void
  useShareModalSettings?: boolean
  editReview?: (product: Product, review: ReviewData) => void
  onClose?: (rated: boolean) => void
  setOrderShownForReview?: (orderId: string) => void
  selectedOrder?: Order
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  order: propOrder,
  controlled,
  isOpen,
  toggleShareModal,
  useShareModalSettings,
  editReview,
  onClose: propOnClose,
  setOrderShownForReview,
  selectedOrder
}) => {
  const [open, setOpen] = useState<boolean>(true)
  const [rated, setRated] = useState<boolean>(false)
  const order = selectedOrder || propOrder

  const { submitReview } = useCreateUserReview()

  useEffect(() => {
    setRated(false)
  }, [isOpen, open])

  const onReview = (data: ReviewData) => {
    setRated(true)

    const product = order && getDataProduct(order, data.productId)

    if (product && order?.id) {
      submitReview(product, order.id, {
        rating: data.rating,
        review: data.review,
        reasons: data.reasons
      } as ReviewInput)
    }
  }

  const onEditReview = (review: ReviewData, productId: string) => {
    setRated(true)

    const product = order && getDataProduct(order, productId)

    if (product && editReview) {
      editReview(product, review)
    }
  }

  const onClose = () => {
    if (controlled && propOnClose) {
      propOnClose(rated)
    } else if (setOrderShownForReview && order?.order_creation_id) {
      setOrderShownForReview(order.order_creation_id)
      setOpen(false)
    }
  }

  const doNotRender = (): boolean => {
    return !order?.id || !(order?.items instanceof Array)
  }

  if (doNotRender()) {
    return null
  }

  const date = order?.display_date
    ? moment(order.display_date).format('dddd, MMM D')
    : ''

  return (
    <Modal
      isOpen={controlled ? isOpen : open}
      onRequestClose={onClose}
      overlayClassName="cookunity__rate_meals_modal_overlay">
      <div className="cookunity__rate_meals_modal">
        <h1 className="title">Rate your order from {date}</h1>
        <h2 className="subtitle">
          Ratings help us provide better meal recommendations and menu selection
          for you.
        </h2>
        <h3 className="text">{CTA_WORDING.RATE_MODAL.TITLE}</h3>
        <p className="description">{CTA_WORDING.RATE_MODAL.TEXT}</p>
        <div className="meals_list">
          {order?.items.map(i => {
            const { product } = i

            // do not rate MARKET items
            if (product?.category === 'MARKET') return null

            if (!product?.id) return null

            return (
              <RateableItem
                key={product.id}
                id={product.id}
                name={product.name ?? ''}
                description={product.short_description ?? ''}
                picture={product.image ?? ''}
                onReview={onReview}
                onEditReview={onEditReview}
                currentReview={product.review}
                {...(toggleShareModal && {
                  onToggleShareModal: (rating: number) =>
                    toggleShareModal({
                      ...(product as Product),
                      stars: rating
                    })
                })}
                useShareModalSettings={useShareModalSettings}
              />
            )
          })}
        </div>

        <CuiButton className="done" onClick={onClose} fill="outline">
          Close
        </CuiButton>
      </div>
    </Modal>
  )
}

export default ReviewModal
